/* Lender · Seller Underwriting roster (SC-5.3 visibility · Feb 2026)
 *
 * Mounted at /lender/seller-underwriting. Shows every seller in the
 * lender's book with their SC-5.3 compliance state:
 *   • W-9 on file  (yes / no)
 *   • COI on file  (yes / no / expired / expiring within 30 days)
 *   • Factoring score + tier (lender-side credit risk)
 *   • Current limit
 *
 * Click a row → drilldown dialog with the full eligibility gate, score
 * breakdown, and direct links to view the actual PDFs (audit-logged).
 */
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import {
  Shield, RefreshCw, Search, FileText, AlertTriangle, ShieldCheck,
  ExternalLink, Calendar, Eye,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";

const fmtMoney = (n) => n == null ? "—" : new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", maximumFractionDigits: 0,
}).format(n);

const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// Build an auth-bearing URL so <a target="_blank"> works for PDF downloads.
// The lender doc endpoint requires Authorization, but for cross-tab opens we
// fall back to a fetch + blob URL.
function ComplianceChip({ ok, label, warn }) {
  const tone = ok ? "emerald" : warn ? "amber" : "rose";
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[10.5px] border bg-${tone}-50 text-${tone}-700 border-${tone}-200 inline-flex items-center gap-1`}
      data-testid={`chip-${label.toLowerCase().replace(/[^a-z]/g, "")}`}
    >
      {ok ? <ShieldCheck className="w-2.5 h-2.5"/> : <AlertTriangle className="w-2.5 h-2.5"/>}
      {label}
    </span>
  );
}

function CoiStatusPill({ row }) {
  if (!row.coi_on_file) return <ComplianceChip ok={false} label="COI missing"/>;
  if (row.coi_expired)  return <ComplianceChip ok={false} label="COI expired"/>;
  // Within 30 days of expiry?
  if (row.coi_expires_at) {
    const days = Math.floor((new Date(row.coi_expires_at) - new Date()) / 86400000);
    if (days <= 30 && days > 0) return <ComplianceChip ok={false} warn label={`COI · ${days}d left`}/>;
  }
  return <ComplianceChip ok={true} label="COI valid"/>;
}

async function downloadDoc(sellerId, docType, sellerCompany) {
  try {
    const tok = localStorage.getItem("token") || "";
    const r = await fetch(`${apiBase}/api/partner/lender/sellers/${sellerId}/documents/${docType}`, {
      headers: { Authorization: `Bearer ${tok}` },
    });
    if (!r.ok) throw new Error(r.statusText);
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    toast.error(`Could not fetch ${docType.toUpperCase()} for ${sellerCompany}`);
  }
}

export default function LenderSellerUnderwriting() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "all") params.status_filter = filter;
      const { data } = await api.get("/partner/lender/sellers", { params });
      setRows(data.rows || []);
      setSummary(data.summary || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not load sellers");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(r =>
      (r.seller_company || "").toLowerCase().includes(s) ||
      (r.seller_email || "").toLowerCase().includes(s) ||
      (r.seller_id || "").toLowerCase().includes(s),
    );
  }, [rows, q]);

  return (
    <div className="p-6 space-y-5" data-testid="lender-seller-underwriting-page">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.2em] text-violet-700 inline-flex items-center gap-1.5">
            <Shield className="w-3 h-3"/> SC-5.3 · Seller Compliance
          </div>
          <h1 className="display text-2xl mt-1">Seller underwriting</h1>
          <p className="text-[12.5px] text-slate-500 mt-1">
            Every seller on your book — W-9, Certificate of Insurance, factoring score and current line.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="seller-uw-refresh">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`}/>Refresh
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-4 gap-3" data-testid="seller-uw-summary">
          <SummaryTile testid="seller-uw-summary-compliant"   label="Compliant"   value={summary.compliant}   tone="emerald"/>
          <SummaryTile testid="seller-uw-summary-missing-w9"  label="Missing W-9" value={summary.missing_w9}  tone="rose"/>
          <SummaryTile testid="seller-uw-summary-missing-coi" label="Missing COI" value={summary.missing_coi} tone="rose"/>
          <SummaryTile testid="seller-uw-summary-coi-expired" label="COI expired" value={summary.coi_expired} tone="amber"/>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2.5">
          <div className="relative flex-1 max-w-sm">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
            <Input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search company, email…"
              data-testid="seller-uw-search"
              className="h-9 pl-9 text-[13px]"
            />
          </div>
          <select
            value={filter}
            onChange={e => setFilter(e.target.value)}
            data-testid="seller-uw-filter"
            className="h-9 rounded-md border border-slate-200 px-2.5 text-[12.5px]"
          >
            <option value="all">All sellers</option>
            <option value="compliant">Compliant only</option>
            <option value="non_compliant">Non-compliant only</option>
            <option value="coi_expiring">COI expiring &lt;30d</option>
          </select>
          <div className="ml-auto text-[11.5px] text-slate-500 mono">{filtered.length} sellers</div>
        </div>

        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-5 py-2.5 font-medium">Seller</th>
              <th className="px-5 py-2.5 font-medium">W-9</th>
              <th className="px-5 py-2.5 font-medium">COI</th>
              <th className="px-5 py-2.5 font-medium">Score · Tier</th>
              <th className="px-5 py-2.5 font-medium">Limit</th>
              <th className="px-5 py-2.5"/>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-500 text-sm">No sellers match your filters.</td></tr>
            ) : filtered.map(r => (
              <tr key={r.seller_id} data-testid={`seller-row-${r.seller_id}`}
                  className={`hover:bg-slate-50 cursor-pointer ${r.compliance_ok ? "" : "bg-rose-50/30"}`}
                  onClick={() => setOpen(r)}>
                <td className="px-5 py-3">
                  <div className="font-medium text-slate-900">{r.seller_company || "—"}</div>
                  <div className="text-[11.5px] text-slate-500 mono">{r.seller_email}</div>
                </td>
                <td className="px-5 py-3">
                  {r.w9_on_file
                    ? <ComplianceChip ok={true}  label="W-9 on file"/>
                    : <ComplianceChip ok={false} label="W-9 missing"/>}
                </td>
                <td className="px-5 py-3"><CoiStatusPill row={r}/></td>
                <td className="px-5 py-3">
                  <div className="tabular-nums display text-[14px]">{r.factoring_score ? Math.round(r.factoring_score) : "—"}</div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wider">{r.starter_tier || "—"}</div>
                </td>
                <td className="px-5 py-3 tabular-nums">{fmtMoney(r.limit_usd)}</td>
                <td className="px-5 py-3 text-right">
                  <Button size="sm" variant="outline" data-testid={`seller-detail-${r.seller_id}`} className="rounded-full">
                    Review<ExternalLink className="w-3 h-3 ml-1"/>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SellerDrilldownDialog
        open={!!open}
        row={open}
        onClose={() => setOpen(null)}
        onView={(docType) => downloadDoc(open.seller_id, docType, open.seller_company)}
      />
    </div>
  );
}

function SummaryTile({ label, value, tone, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="text-[10px] mono uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className={`display text-2xl mt-1 text-${tone}-700`}>{value ?? 0}</div>
    </div>
  );
}

function SellerDrilldownDialog({ open, row, onClose, onView }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !row) { setDetail(null); return; }
    setLoading(true);
    api.get(`/partner/lender/sellers/${row.seller_id}`)
      .then(({ data }) => setDetail(data))
      .catch(() => toast.error("Could not load detail"))
      .finally(() => setLoading(false));
  }, [open, row]);

  if (!row) return null;
  const docs = detail?.docs || row;
  const elig = detail?.eligibility;
  const score = detail?.score;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-2xl" data-testid="seller-drilldown-dialog">
        <DialogHeader>
          <DialogTitle className="display text-xl">{row.seller_company}</DialogTitle>
          <DialogDescription className="text-[12.5px] text-slate-500">{row.seller_email}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-slate-400 text-sm">Loading…</div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3">
              {/* W-9 */}
              <div className={`rounded-xl border p-4 ${docs.w9_on_file ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] mono uppercase tracking-[0.18em] text-slate-600">W-9 form</div>
                  {docs.w9_on_file
                    ? <ShieldCheck className="w-4 h-4 text-emerald-600"/>
                    : <AlertTriangle className="w-4 h-4 text-rose-600"/>}
                </div>
                <div className="display text-base mt-2">
                  {docs.w9_on_file ? "On file" : "Missing"}
                </div>
                {docs.w9_uploaded_at && (
                  <div className="text-[11px] text-slate-500 mt-1">Uploaded {new Date(docs.w9_uploaded_at).toLocaleDateString()}</div>
                )}
                {docs.w9_on_file && (
                  <Button size="sm" variant="outline" onClick={() => onView("w9")}
                          data-testid="view-w9-btn"
                          className="rounded-full mt-3 w-full">
                    <Eye className="w-3.5 h-3.5 mr-1"/>View PDF
                  </Button>
                )}
              </div>

              {/* COI */}
              <div className={`rounded-xl border p-4 ${
                docs.coi_on_file && !docs.coi_expired ? "border-emerald-200 bg-emerald-50" :
                docs.coi_expired ? "border-amber-200 bg-amber-50" : "border-rose-200 bg-rose-50"
              }`}>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] mono uppercase tracking-[0.18em] text-slate-600">Certificate of Insurance</div>
                  {docs.coi_on_file && !docs.coi_expired
                    ? <ShieldCheck className="w-4 h-4 text-emerald-600"/>
                    : <AlertTriangle className="w-4 h-4 text-rose-600"/>}
                </div>
                <div className="display text-base mt-2">
                  {!docs.coi_on_file ? "Missing"
                    : docs.coi_expired ? "Expired"
                    : "On file"}
                </div>
                {docs.coi_expires_at && (
                  <div className="text-[11px] text-slate-500 mt-1 inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3"/>
                    {docs.coi_expired ? "Expired " : "Expires "}
                    {new Date(docs.coi_expires_at).toLocaleDateString()}
                  </div>
                )}
                {docs.coi_on_file && (
                  <Button size="sm" variant="outline" onClick={() => onView("coi")}
                          data-testid="view-coi-btn"
                          className="rounded-full mt-3 w-full">
                    <Eye className="w-3.5 h-3.5 mr-1"/>View PDF
                  </Button>
                )}
              </div>
            </div>

            {/* Eligibility gate */}
            {elig && (
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="text-[10px] mono uppercase tracking-[0.18em] text-slate-500">Eligibility</div>
                    <div className="display text-base mt-0.5">
                      {elig.eligible
                        ? <span className="text-emerald-700">Approved for factoring</span>
                        : <span className="text-rose-700">Locked</span>}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {elig.gates.map(g => (
                    <div key={g.key} className="flex items-center gap-2 text-[12px]">
                      {g.ok
                        ? <ShieldCheck className="w-3 h-3 text-emerald-600 shrink-0"/>
                        : <AlertTriangle className={`w-3 h-3 shrink-0 ${g.kind === "hard" ? "text-rose-600" : "text-amber-600"}`}/>}
                      <span className={g.ok ? "text-slate-600" : "text-slate-900 font-medium"}>{g.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Score */}
            {score && (
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="text-[10px] mono uppercase tracking-[0.18em] text-slate-500">Factoring score</div>
                <div className="flex items-baseline gap-2 mt-1">
                  <span className="display text-2xl tabular-nums">{Math.round(score.composite_score)}</span>
                  <span className="text-[11px] text-slate-400 mono">/ 850</span>
                  <span className="chip uppercase tracking-wider text-[10px] bg-violet-50 text-violet-700 border-violet-200">{score.tier}</span>
                </div>
                <div className="mt-2 text-[12px] text-slate-500">
                  Methodology {score.methodology_version} · {score.sample_size} invoices · sized to {fmtMoney(score.starter_limit_usd)}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
